import React, { useState } from "react";
import classNames from "classnames";
import { useTranslation } from "react-i18next";
import { faPalette } from "@fortawesome/pro-regular-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { BottomSheet } from "components/bottomSheetDialog";
import { useOptionalBottomSheetContext } from "components/bottomSheetDialog/bottomSheetContext";
import { CustomColorPickerPopover } from "./CustomColorPickerPopover";
import { ColorPickerContent } from "./popup/ColorPickerContent";
import "./customColorPickerPopover.scss";
import "./popup/colorPickerPopup.scss";
import "./customColorPickerBottomSheet.scss";

interface ICustomColorPickerBottomSheetProps {
  title: string;
  hexColor: string;
  updateHexColor: (updatedHexColor: string | null) => void;
  portalParentId?: string;
  fullWidth?: boolean;
  onGestureStart?: () => void;
  onGestureEnd?: () => void;
}

function NestedColorPickerBottomSheet(props: ICustomColorPickerBottomSheetProps) {
  const { title, hexColor, updateHexColor, fullWidth = false, onGestureStart, onGestureEnd } = props;
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);

  return (
    <>
      <div
        className={classNames("color-picker", {
          "color-picker-popup-open": open,
          "color-picker--full-width": fullWidth,
        })}
        onClick={() => setOpen(true)}
      >
        <div className="color-picker-color-container">
          <div className="color-picker-color" style={{ backgroundColor: hexColor }}>
            {hexColor == null && <div className="color-picker-color-slash" />}
          </div>
          <div className="color-picker-title">{title}</div>
        </div>
        <FontAwesomeIcon className="color-picker-palette-icon" icon={faPalette} />
      </div>
      <BottomSheet.Root
        open={open}
        onOpenChange={nextOpen => {
          if (!nextOpen) {
            setOpen(false);
          }
        }}
        dismissible
        modal
        repositionInputs
      >
        <BottomSheet.Portal>
          <BottomSheet.Backdrop />
          <BottomSheet.Popup className="color-picker-bottom-sheet">
            <BottomSheet.Header>
              <BottomSheet.Title>{title}</BottomSheet.Title>
              <BottomSheet.Close aria-label={t("Close")} />
            </BottomSheet.Header>
            <BottomSheet.Body>
              <ColorPickerContent
                hexColor={hexColor}
                updateHexColor={updateHexColor}
                className={classNames("color-picker-controls", {
                  "color-picker-controls--full-width": fullWidth,
                })}
                onGestureStart={onGestureStart}
                onGestureEnd={onGestureEnd}
              />
            </BottomSheet.Body>
          </BottomSheet.Popup>
        </BottomSheet.Portal>
      </BottomSheet.Root>
    </>
  );
}

/**
 * Drop-in for CustomColorPickerPopover when the picker may render under BottomSheet.Root.
 * Nested bottom sheet inside a parent sheet; same popover elsewhere.
 */
export function CustomColorPickerBottomSheet(props: ICustomColorPickerBottomSheetProps) {
  const parentSheet = useOptionalBottomSheetContext();
  if (parentSheet === null) {
    return <CustomColorPickerPopover {...props} />;
  }
  return <NestedColorPickerBottomSheet {...props} />;
}
